<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <!-- 헤더 -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900">프로필 관리</h1>
      <p class="mt-2 text-gray-600">개인 정보를 수정하고 계정을 관리하세요</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- 프로필 요약 -->
      <div class="lg:col-span-1">
        <div class="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
          <div class="text-center">
            <div class="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span class="text-primary-600 font-bold text-2xl">
                {{ user?.name?.charAt(0).toUpperCase() }}
              </span>
            </div>
            <h3 class="text-xl font-semibold text-gray-900">{{ user?.name }}</h3>
            <p class="text-gray-600 text-sm">{{ user?.email }}</p>
            <span
              class="inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-2"
              :class="{
                'bg-red-100 text-red-800': user?.role === 'ADMIN',
                'bg-blue-100 text-blue-800': user?.role === 'USER'
              }"
            >
              {{ user?.role === 'ADMIN' ? '관리자' : '일반 사용자' }}
            </span>
          </div>
          
          <div class="mt-6 pt-6 border-t space-y-3">
            <div class="flex justify-between text-sm">
              <span class="text-gray-600">가입일</span>
              <span class="text-gray-900">{{ formatDate(user?.createdAt) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-600">최종 수정</span>
              <span class="text-gray-900">{{ formatDate(user?.updatedAt) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 프로필 수정 폼 -->
      <div class="lg:col-span-2 space-y-6">
        <!-- 기본 정보 수정 -->
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">기본 정보</h2>
          
          <form @submit.prevent="updateProfile" class="space-y-6">
            <!-- 이메일 (수정 불가) -->
            <div>
              <label class="form-label">이메일</label>
              <input
                :value="user?.email"
                type="email"
                disabled
                class="form-input bg-gray-50 text-gray-500 cursor-not-allowed"
              />
              <p class="form-hint">이메일은 변경할 수 없습니다</p>
            </div>

            <!-- 이름 -->
            <div>
              <label for="name" class="form-label">이름 *</label>
              <input
                id="name"
                v-model="profileForm.name"
                type="text"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.name }"
                placeholder="홍길동"
              />
              <p v-if="errors.name" class="form-error">{{ errors.name }}</p>
            </div>

            <!-- 전화번호 -->
            <div>
              <label for="phone" class="form-label">전화번호 *</label>
              <input
                id="phone"
                v-model="profileForm.phone"
                type="tel"
                required
                class="form-input"
                :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.phone }"
                placeholder="010-1234-5678"
              />
              <p v-if="errors.phone" class="form-error">{{ errors.phone }}</p>
              <p class="form-hint">'-'를 포함하여 입력해주세요</p>
            </div>

            <!-- 수정 버튼 -->
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="isUpdatingProfile"
                class="btn btn-primary btn-sm"
                :class="{ 'opacity-50 cursor-not-allowed': isUpdatingProfile }"
              >
                <span v-if="isUpdatingProfile" class="flex items-center">
                  <div class="spinner w-4 h-4 mr-2"></div>
                  수정 중...
                </span>
                <span v-else>프로필 수정</span>
              </button>
            </div>

            <div v-if="errors.general" class="bg-red-50 border border-red-200 rounded-md p-3">
              <p class="text-red-600 text-sm">{{ errors.general }}</p>
            </div>
          </form>
        </div>

        <!-- 비밀번호 변경 -->
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <h2 class="text-xl font-semibold text-gray-900 mb-6">비밀번호 변경</h2>
          
          <form @submit.prevent="changePassword" class="space-y-6">
            <!-- 현재 비밀번호 -->
            <div>
              <label for="currentPassword" class="form-label">현재 비밀번호 *</label>
              <div class="relative">
                <input
                  id="currentPassword"
                  v-model="passwordForm.currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  required
                  class="form-input pr-10"
                  :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.currentPassword }"
                  placeholder="현재 비밀번호를 입력하세요"
                />
                <button
                  type="button"
                  @click="showCurrentPassword = !showCurrentPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon v-if="!showCurrentPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="errors.currentPassword" class="form-error">{{ errors.currentPassword }}</p>
            </div>

            <!-- 새 비밀번호 -->
            <div>
              <label for="newPassword" class="form-label">새 비밀번호 *</label>
              <div class="relative">
                <input
                  id="newPassword"
                  v-model="passwordForm.newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  required
                  class="form-input pr-10"
                  :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.newPassword }"
                  placeholder="8자 이상, 영문자와 숫자 포함"
                />
                <button
                  type="button"
                  @click="showNewPassword = !showNewPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon v-if="!showNewPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="errors.newPassword" class="form-error">{{ errors.newPassword }}</p>
            </div>

            <!-- 비밀번호 확인 -->
            <div>
              <label for="confirmPassword" class="form-label">비밀번호 확인 *</label>
              <div class="relative">
                <input
                  id="confirmPassword"
                  v-model="passwordForm.confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  class="form-input pr-10"
                  :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.confirmPassword }"
                  placeholder="새 비밀번호를 다시 입력하세요"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  <EyeIcon v-if="!showConfirmPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="errors.confirmPassword" class="form-error">{{ errors.confirmPassword }}</p>
            </div>

            <!-- 변경 버튼 -->
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="isChangingPassword"
                class="btn btn-primary btn-sm"
                :class="{ 'opacity-50 cursor-not-allowed': isChangingPassword }"
              >
                <span v-if="isChangingPassword" class="flex items-center">
                  <div class="spinner w-4 h-4 mr-2"></div>
                  변경 중...
                </span>
                <span v-else>비밀번호 변경</span>
              </button>
            </div>

            <div v-if="errors.password" class="bg-red-50 border border-red-200 rounded-md p-3">
              <p class="text-red-600 text-sm">{{ errors.password }}</p>
            </div>
          </form>
        </div>

        <!-- 계정 삭제 -->
        <div class="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 class="text-xl font-semibold text-red-900 mb-4">계정 삭제</h2>
          <p class="text-red-700 text-sm mb-6">
            계정을 삭제하면 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.
            신중하게 결정해주세요.
          </p>
          <button
            @click="showDeleteModal = true"
            class="btn bg-red-600 hover:bg-red-700 text-white btn-sm"
          >
            계정 삭제
          </button>
        </div>
      </div>
    </div>

    <!-- 계정 삭제 확인 모달 -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold text-red-900">계정 삭제 확인</h3>
          <button @click="showDeleteModal = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <div class="space-y-4">
          <div class="bg-red-50 p-4 rounded-md">
            <ExclamationTriangleIcon class="w-5 h-5 text-red-400 inline mr-2" />
            <span class="text-red-800 font-medium">이 작업은 되돌릴 수 없습니다!</span>
          </div>
          
          <p class="text-gray-700">
            정말로 계정을 삭제하시겠습니까? 모든 게시글, 예약 정보, 개인 데이터가 영구적으로 삭제됩니다.
          </p>
          
          <div class="flex gap-3 pt-4">
            <button
              @click="showDeleteModal = false"
              class="btn btn-outline btn-sm flex-1"
            >
              취소
            </button>
            <button
              @click="deleteAccount"
              :disabled="isDeletingAccount"
              class="btn btn-sm bg-red-600 hover:bg-red-700 text-white flex-1"
              :class="{ 'opacity-50 cursor-not-allowed': isDeletingAccount }"
            >
              <span v-if="isDeletingAccount">삭제 중...</span>
              <span v-else>삭제하기</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import {
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

const user = computed(() => authStore.user)
const isUpdatingProfile = ref(false)
const isChangingPassword = ref(false)
const isDeletingAccount = ref(false)
const showDeleteModal = ref(false)

// 비밀번호 표시 상태
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

// 폼 데이터
const profileForm = reactive({
  name: '',
  phone: ''
})

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// 에러 상태
const errors = reactive({
  name: '',
  phone: '',
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  general: '',
  password: ''
})

// 날짜 포맷
const formatDate = (dateString?: string) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

// 에러 초기화
const clearErrors = () => {
  Object.keys(errors).forEach(key => {
    errors[key as keyof typeof errors] = ''
  })
}

// 프로필 수정
const updateProfile = async () => {
  clearErrors()
  
  if (!profileForm.name.trim()) {
    errors.name = '이름을 입력해주세요.'
    return
  }
  
  if (profileForm.name.length < 2) {
    errors.name = '이름은 2자 이상이어야 합니다.'
    return
  }
  
  if (!profileForm.phone.trim()) {
    errors.phone = '전화번호를 입력해주세요.'
    return
  }
  
  if (!/^01(?:0|1|[6-9])-(?:\d{3}|\d{4})-\d{4}$/.test(profileForm.phone)) {
    errors.phone = '올바른 전화번호 형식을 입력해주세요.'
    return
  }

  try {
    isUpdatingProfile.value = true
    const success = await authStore.updateProfile({
      name: profileForm.name,
      phone: profileForm.phone
    })
    
    if (success) {
      toast.success('프로필이 수정되었습니다.')
    }
  } catch (error: any) {
    errors.general = '프로필 수정에 실패했습니다. 다시 시도해주세요.'
    toast.error('프로필 수정에 실패했습니다.')
  } finally {
    isUpdatingProfile.value = false
  }
}

// 비밀번호 변경
const changePassword = async () => {
  clearErrors()
  
  if (!passwordForm.currentPassword) {
    errors.currentPassword = '현재 비밀번호를 입력해주세요.'
    return
  }
  
  if (!passwordForm.newPassword) {
    errors.newPassword = '새 비밀번호를 입력해주세요.'
    return
  }
  
  if (passwordForm.newPassword.length < 8) {
    errors.newPassword = '비밀번호는 8자 이상이어야 합니다.'
    return
  }
  
  if (!/^(?=.*[A-Za-z])(?=.*\d)/.test(passwordForm.newPassword)) {
    errors.newPassword = '비밀번호는 영문자와 숫자를 포함해야 합니다.'
    return
  }
  
  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    errors.confirmPassword = '비밀번호가 일치하지 않습니다.'
    return
  }

  try {
    isChangingPassword.value = true
    const success = await authStore.changePassword({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword
    })
    
    if (success) {
      toast.success('비밀번호가 변경되었습니다.')
      // 폼 초기화
      passwordForm.currentPassword = ''
      passwordForm.newPassword = ''
      passwordForm.confirmPassword = ''
    }
  } catch (error: any) {
    if (error.response?.status === 400) {
      errors.password = '현재 비밀번호가 올바르지 않습니다.'
    } else {
      errors.password = '비밀번호 변경에 실패했습니다. 다시 시도해주세요.'
    }
    toast.error('비밀번호 변경에 실패했습니다.')
  } finally {
    isChangingPassword.value = false
  }
}

// 계정 삭제
const deleteAccount = async () => {
  try {
    isDeletingAccount.value = true
    // 계정 삭제 API 호출 (미구현 상태에서는 로그아웃만)
    toast.success('계정이 삭제되었습니다.')
    authStore.logout()
    router.push('/')
  } catch (error: any) {
    toast.error('계정 삭제에 실패했습니다.')
  } finally {
    isDeletingAccount.value = false
    showDeleteModal.value = false
  }
}

// 컴포넌트 마운트 시 폼에 사용자 정보 설정
onMounted(() => {
  if (user.value) {
    profileForm.name = user.value.name
    profileForm.phone = user.value.phone
  }
})
</script>
